import React, { FunctionComponent, ReactElement, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import { Image } from '@empiriecom/module-components/Image';
import { Modal } from '@empiriecom/module-components/Modal';
import { Esi } from '@empiriecom/module-components/Esi';
import { AttributeValueObject } from '@/src/interfaces';
import useUrlParameters from '../hooks/useUrlParameters';

const PremiumServiceWrapper = styled.div<{ mobile: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  ${({ mobile }) => (mobile ? 'margin: 0.75rem;' : 'margin-bottom: 1.5rem;')};
`;

const PremiumIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  margin-right: 0.7rem;
`;
const PremiumText = styled.div`
  text-decoration: underline;
`;

export interface PremiumServiceProps {
  /* The service values to render. */
  value: AttributeValueObject;
}

/**
 * <p>This is the premium service component. It renders the passed premium service.
 * If the passed service has a link it will also enable the click handler for the component which opens a modal.</p>
 */
export const PremiumService: FunctionComponent<PremiumServiceProps> = ({
  value: { link, iconUrl, text },
}): ReactElement => {
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState<boolean>(false);
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const esiUrl = useUrlParameters(link);
  return (
    <>
      <PremiumServiceWrapper
        onClick={() => link && setIsPremiumModalOpen(true)}
        data-testid="premium-wrapper"
        mobile={isMobile}
      >
        {iconUrl && (
          <PremiumIcon>
            <Image alt={text} src={iconUrl} fallback={() => null} />
          </PremiumIcon>
        )}
        <PremiumText>
          <FormattedMessage
            id="premium-service.label"
            defaultMessage="inkl. {servicename}"
            values={{ servicename: text }}
          />
        </PremiumText>
      </PremiumServiceWrapper>
      {link && (
        <Modal
          headline={text}
          onRequestClose={() => setIsPremiumModalOpen(false)}
          isOpen={isPremiumModalOpen}
        >
          <div data-testid="premium-modal-esi">
            <Esi src={esiUrl} />
          </div>
        </Modal>
      )}
    </>
  );
};

export default PremiumService;
